/**
 * Decide quando uma rajada de saída do terminal foi uma resposta que terminou.
 *
 * O TUI não avisa "acabei". Enquanto o agente trabalha, o PTY fica recebendo
 * redesenhos. Quando isso para por um tempo, a resposta acabou. A pintura
 * inicial, o eco de uma tecla e o redesenho de um resize não contam.
 */

export const QUIET_MS = 1_800;
const STARTUP_MS = 4_000;

export interface TurnState {
  born: number;
  talked: boolean;
  resizeAt: number;
  burst: { start: number; last: number; bytes: number } | null;
}

export function createTurn(now: number): TurnState {
  return { born: now, talked: false, resizeAt: 0, burst: null };
}

/** Texto que o usuário mandou para o agente, não seta nem tecla de controle. */
export function noteInput(state: TurnState, data: string): TurnState {
  if (!/[^\x00-\x1f\x7f]/.test(data) && !/[\r\n]/.test(data)) return state;
  if (state.talked) return state;
  return { ...state, talked: true };
}

export function noteResize(state: TurnState, now: number): TurnState {
  return { ...state, resizeAt: now };
}

export function noteOutput(state: TurnState, bytes: number, now: number): TurnState {
  if (bytes <= 0) return state;
  const burst = state.burst
    ? { start: state.burst.start, last: now, bytes: state.burst.bytes + bytes }
    : { start: now, last: now, bytes };
  return { ...state, burst };
}

/**
 * Chamado depois de QUIET_MS sem saída nova. `settled` significa que vale avisar.
 * A rajada é consumida de qualquer forma, para o próximo ciclo começar limpo.
 */
export function noteQuiet(state: TurnState, now: number): { state: TurnState; settled: boolean } {
  const burst = state.burst;
  if (burst && now < burst.last) return { state, settled: false };
  if (!burst) return { state, settled: false };
  const span = Math.max(0, burst.last - burst.start);
  const cleared: TurnState = { ...state, burst: null };

  const afterResize =
    state.resizeAt > 0 && burst.start >= state.resizeAt && burst.start - state.resizeAt < 800;
  if (afterResize && span < 700) return { state: cleared, settled: false };

  const startup = !state.talked && burst.start < state.born + STARTUP_MS && span < 1_500;
  if (startup) return { state: cleared, settled: false };

  if (burst.bytes < 120 && span < 350) return { state: cleared, settled: false };
  if (span < 280 && burst.bytes < 400) return { state: cleared, settled: false };

  return { state: cleared, settled: true };
}
